export enum ConversationStatus {
  NOTSTARTED = 0,
  PENDING = 10,
  STARTED = 20,
  PAUSED = 30,
  COMPLETED = 40,
  EXITED = 50,
  ERRORED = 60,
  FLAGGED = 70,
  FAILED_EVAL = 80,
  // Created at screener completion with the answers in
  // conversations.screener_completes. SCREENED rows are adopted by the LiveKit
  // agent (flipped to STARTED) when the interview starts; SCREENED_OUT is
  // terminal.
  SCREENED = 90,
  SCREENED_OUT = 100
}

export enum Plan {
  STARTER = 10,
  PREMIUM = 20,
  ENTERPRISE = 30
}

export enum InterviewStatus {
  DRAFT = 10, // Can only be turned on prior to collecting.
  LIVE = 20, // Only status where new conversations are not hidden.
  COMPLETE = 21, // No longer collecting data
  ARCHIVED = 30 // Soft delete, interview is not visible in portal
}

export enum InterviewPrimedStatus {
  REQUIRES_PRIMING = 0,
  PRIMING = 1,
  PRIMED = 2,
  // A priming run holds the interview but has not started working yet — it is
  // sleeping out the debounce, or was only just started.
  PRIMING_SOON = 3
}

// Where an interview stands with its end-of-interview report. A report run
// writes its row only when it finishes, so this is what says a run is under
// way, and what tells a run that died from one that never started.
export enum InterviewReportStatus {
  NONE = 0,
  GENERATING = 1,
  READY = 2,
  FAILED = 3
}

export interface RecordingEvent {
  // `topic_skipped` marks a topic the flow passed over without running it:
  // `payload.source` is 'skip_if' (its skip condition held — with the
  // `condition` and the judge's `reason`) or 'user' (the next-topic button).
  type: 'user' | 'assistant' | 'user_input' | 'topic_skipped';
  topic?: string;
  topic_guid?: string;
  content?: string;
  // A survey answer (`user_input`) is the chosen option / typed text as a
  // plain string; other event types carry an object.
  payload?: Record<string, any> | string;
  from_start?: number;
  duration?: number;
  timestamp?: string;

  seconds_from_start?: number; // Deprecated
  // emotion?: Record<string, any>; // Deprecated
}

export enum InterviewModality {
  AUDIO = 1,
  VIDEO = 2,
  TEXT = 3,
  PHONE = 4,
  // Experimental: respondent speaks (voice input), assistant replies in text only.
  TEXT_VOICE = 5,
  // Guided layout only: provisions the switchable superset (mic + TTS) and lets
  // the respondent toggle voice/video/text mid-interview. Not offered for any other
  // layout — see InterviewLayout.GUIDED.
  ALL = 6
}

export enum InterviewFallbackModality {
  NONE = 1,
  AUDIO = 2,
  TEXT = 3,
  AUDIO_OR_TEXT = 4
}

export enum InterviewDeviceSupport {
  ALL_DEVICES = 10,
  MOBILE_ONLY = 20,
  DESKTOP_ONLY = 30
}

export enum TopicArchetype {
  INQUISITOR = 10,
  PROCEDURALIST = 20,
  CHALLENGER = 30,
  CONVERSATIONALIST = 40
}

export enum TopicType {
  VOICE = 10,
  QUESTION = 11,
  STATEMENT = 12,

  SURVEY_TEXT = 20,
  SURVEY_MULTI_CHOICE = 21,
  SURVEY_BOOLEAN = 22,
  SURVEY_DROPDOWN = 23,

  SYSTEM_PROMPT_OVERRIDE = 30
}

export enum MediaType {
  IMAGE = 10,
  VIDEO = 20,
  IFRAME = 30
}

export enum PromptType {
  // Voice Topic Types
  INQUISITOR = 10,
  PROCEDURALIST = 20,
  CHALLENGER = 30,
  CONVERSATIONALIST = 40,

  // Non Voice Types
  QUESTION = 50,
  STATEMENT = 60,
  SURVEY = 70
}

/**
 * A topic's override of the agent's idle timer: seconds of silence before the
 * moderator asks "Are you still there?". The agent ends the interview after 4×
 * this.
 */
export type TopicIdleTimer = {
  promptSeconds?: number;
};

/**
 * Conditional skip: the topic is passed over when `condition` holds for the
 * respondent. The agent judges it on entry to the topic against the screener
 * answers, survey answers and transcript so far; when in doubt the topic runs.
 */
export type TopicSkipIf = {
  condition: string;
};

export type InterviewTopic = {
  guid?: string;
  name: string;
  type: TopicType;

  // Common properties
  context?: string;
  sysprompt?: string;

  // Voice properties
  archetype?: TopicArchetype;
  maxTime?: number;
  goals?: string[];
  examples?: string[];

  // Statement properties
  statement?: string;

  // Question properties
  question?: string;
  probeQuestions?: string[];
  probeDepth?: number;

  // Survey properties
  options?: string[];

  // Stimulus properties
  media?: {
    type: MediaType;
    url?: string; // url deprecated in favor of storageKey
    storageKey?: string;
    fileSizeMB?: number;
  };

  // Determines if the topic has previous state or not
  memoryless?: boolean;

  // Overrides the agent's idle timer while this topic is active. Absent → the
  // defaults ("Are you still there?" after 15s, end after 60s of silence).
  idleTimer?: TopicIdleTimer;

  // Inserts the respondent's longitudinal context (respondents.context) into
  // the topic's system prompt when the conversation is tied to a respondent.
  useRespondentContext?: boolean;

  primedContext?: string;

  // Skip this topic for respondents the condition applies to. Absent → the
  // topic always runs.
  skipIf?: TopicSkipIf;
};

export type InterviewConfig = {
  id?: string;
  topics: InterviewTopic[];
  context?: string;

  signoff?: string; // Deprecated
  kickoff?: string; // Deprecated
};

export enum VoiceProvider {
  DAILY = 1,
  HUME = 2,
  VAPI = 3
}

/** Interview Code Types */
export enum CodeDataType {
  TEXT = 1,
  BOOLEAN = 2,
  NUMBER = 3,
  FLOAT = 4
}

export enum CodeSourceTypeEnum {
  PROMPT = 10,
  HUMAN = 20,
  SYSTEM = 30,
  URL_PARMS = 40,
  RESPONDENT = 50,
  // Screener answers imported from an external form (Typeform), which store the
  // question title and the answer text verbatim.
  SCREENER = 60,
  // Answers to this interview's own screener questions. The codetype's `name`
  // holds the question's guid and the code's `value` holds the selected option's
  // guid — both resolved for display against `interviews.screener_questions`,
  // which is the single source of truth for the wording.
  SCREENER_QUESTION = 61,
  USER_EVAL = 70,
  USER_EVAL_TAGGED = 71, // Tagged to be seen in analysis
  TOPIC = 80,
  REPORT = 90
}

/* Analyze Queries */
export enum QueryStrategy {
  AUTO = 1,
  ALL = 2,
  RAG = 3,
  CHAINED_LLM = 4,
  SAMPLE = 5
}

// A single classification option. `label` is the canonical value stored as the
// answer; `description` is an optional clarifier the classifying model uses to
// understand what the option means (it never becomes an answer value).
export interface CodeOption {
  label: string;
  description: string | null;
}

export interface QueryRequest {
  question: string;
  codify: boolean;
  datatype: CodeDataType;
  multiple?: boolean;
  // Classification options as { label, description } objects. Legacy rows may
  // store a plain string[] (labels only); normalizeOptions() upgrades those at
  // every DB / request boundary, so in-memory consumers always see CodeOption[].
  options?: CodeOption[] | null;
  strategy?: QueryStrategy;
  conversationIds?: number[];
  topic?: string;
  // Free-text clarification of the question's intent, appended to the question
  // when prompting. Reduces ambiguity between the human and the model.
  questionContext?: string;
  // Use the evaluation prompt variant instead of the standard codify prompt.
  // Set by the eval code form.
  evaluate?: boolean;
}

export interface QueryResponse {
  request: QueryRequest;
  conversations?: AnnotatedConversation[];
  answer?: string;
}

type scalar = string | number | boolean;

interface AnnotatedValue {
  answer: scalar;
  quote?: string;
  index?: number;
  confidence_score?: number;
}

export interface AnnotatedConversation {
  id: number;
  values?: AnnotatedValue[] | AnnotatedValue;
}

export interface QuantGraphRow {
  label: string;
  count: number;
  highlight?: boolean;
  referenceCount?: number;
}

export interface Workspace {
  id: string;
  name: string;
  personal: boolean;
  setActive?: () => Promise<void>;
}

export enum PulsePlan {
  FREE = 10,
  PAID = 20
}

export interface CompleteConfig {
  pulseBranding: boolean;
  completeMsg?: string;
  exitMsg?: string;
  completeRedirect?: string;
  exitRedirect?: string;
  // Markdown shown on the interview link once it stops accepting responses.
  // Unset falls back to the `notOpen` string for the interview's language.
  closedMsg?: string;
  // Cap on completed conversations. Once the interview has this many, it closes
  // itself (status COMPLETE) and its active panel campaigns are paused. Unset —
  // the key absent entirely — means no cap.
  maxCompletes?: number;
}

export enum PanelType {
  PROLIFIC = 10,
  // Everyone recruited through Respondent, whichever campaign brought them in.
  RESPONDENT = 20,
  // INVARIANT: campaign-only. `panel_campaigns.panel` may be either of these —
  // they pick the provider audience, screener filters and reward rules — but
  // `respondents.panel` must NEVER be. A Respondent person is always stored
  // under RESPONDENT (20).
  //
  // The unique key on respondents is (panel, panelist_id, workspace_id), so one
  // participant_id written under two panel values becomes two rows for one
  // human: their conversations split across both, and deleting one leaves the
  // other live and still matching their interview links.
  //
  // Never derive a RESPONDENT_* panel from the campaign behind their link. Take
  // it from getPanelistIdMatch and pass it through untouched.
  RESPONDENT_B2B = 21,
  RESPONDENT_B2C = 22,
  ENGAGE = 30,
  // Respondents identified by a `uid` query param on a direct interview link,
  // outside any recruiting panel.
  CUSTOM = 40
}

export enum PanelCampaignStatus {
  DRAFT = 10,
  ACTIVE = 20,
  PAUSED = 30,
  COMPLETED = 40
}

export interface InterviewTheme {
  showLogo?: boolean;
  showExit?: boolean;
  showProgress?: boolean;
  showTitle?: boolean;
  showCreatePulseButton?: boolean;
  showTitleOnBrief?: boolean;
  primaryColor?: string;
  btnColor?: string;
  prominentLogo?: boolean;
  logoUrl?: string;
  captionButtonText?: string;
}

export enum InterviewLayout {
  SPEAKER = 'speaker',
  STIMULUS = 'stimulus',
  UXR = 'uxr',
  // Sidebar + main-pane layout with a runtime voice/video/text mode switch.
  // Coupled to InterviewModality.ALL (the only modality it runs).
  GUIDED = 'guided'
}

/** Which camera a respondent on a phone or tablet films with. */
export enum MobileCameraSource {
  REAR = 'rear',
  FRONT = 'front',
  // Front camera by default, and the respondent may switch. Never stored:
  // display_config leaves the key out instead.
  EITHER = 'either'
}

export interface InterviewDisplayConfig {
  theme?: string;
  layout?: InterviewLayout;
  primaryColor?: string;
  btnColor?: string;
  prominentLogo?: boolean;
  showCreatePulseButton?: boolean;
  showTitleOnBrief?: boolean;
  logo?: string;
  fallbackModality?: InterviewFallbackModality | null;
  captionButtonText?: string;
  // Locks respondents on a phone or tablet to the rear or front camera. Only
  // saved on Mobile Only interviews; absent means Either.
  mobileCameraSource?: MobileCameraSource.REAR | MobileCameraSource.FRONT;
  // Wording on the card that invites a respondent into the interview once they
  // pass the screener. Each replaces its translated, modality-specific default
  // and is shown exactly as typed, whatever mode the respondent is in.
  pivotHeadline?: string;
  pivotBody?: string;
}

export interface InterviewConsentConfig {
  // Whether the consent step is shown before the interview starts. Undefined is
  // treated as enabled so existing interviews keep their consent screen.
  enabled?: boolean;
  consent: string;
  checkboxes?: string[];
  button?: string;
}

export interface InterviewLanguageConfig {
  language: string;
  locale: string;
  // Custom system-prompt language directive. When set, it replaces the
  // locale-derived language suffix the agent appends to its system prompt.
  prompt_suffix?: string;
}

export enum InterviewLanguage {
  EN_US = 'en-US',
  ES_ES = 'es-ES',
  ES_MX = 'es-MX',
  FR_FR = 'fr-FR',
  PT_BR = 'pt-BR',
  HI_IN = 'hi-IN',
  SW_KE = 'sw-KE',
  TL_PH = 'tl-PH'
}

/**
 * Respondent-facing copy for the reward screens, overriding the translated
 * defaults per interview. Every field is optional and a blank value falls back
 * to the default, so clearing a field in the configure UI restores the
 * translation rather than blanking the screen.
 *
 * Every field accepts the `{reward}` and `{email}` placeholders (see
 * `utils/pulse/rewardCopy.ts`). `{email}` only has a value once the respondent
 * has submitted it, so on the claim screen it renders as nothing.
 *
 * Keys are flat, with the `claimed*` prefix marking the confirmation screen —
 * the configure UI groups them for display without the storage shape having to
 * track that grouping.
 */
export interface InterviewRewardCopy {
  heading?: string;
  /** The caption under the heading. Labelled "Caption" in the configure UI. */
  body?: string;
  /**
   * Markdown filling the slot the benefit bullets occupy, labelled "Body" in the
   * configure UI. Setting it replaces that list outright, icons included; while
   * it is empty the fixed three-line list renders instead.
   */
  details?: string;
  cta?: string;
  claimedHeading?: string;
  claimedBody?: string;
  closePage?: string;
  /** Holding SMS for a rewarded phone interview. Phone modality only — there is
   *  no claim screen on that path, so none of the fields above are used. */
  phoneSms?: string;
}

export interface InterviewRewardConfig {
  reward?: number;
  copy?: InterviewRewardCopy;
}

export interface InterviewEventRecord {
  event_dt: string;
}

/**
 * interview_events.events shape — one attribute per tracked event type, each
 * holding the respondent's most recent occurrence of it. A key is present only
 * when that event actually fired: a stage the respondent never reached, or that
 * their flow doesn't include, is absent rather than null.
 *
 * Which stages a flow includes varies by modality and config — see
 * getPreInterviewSequence in components/interview/livekit/preInterviewFlow.ts.
 * `screener_start`/`screener_complete`/`pivot` need screener questions, `pivot`
 * is web-only, `device_check` is skipped for text and phone, and `consent` is
 * dropped for text without custom consent and when consent is disabled.
 *
 * Most fire when a stage is *presented*; `screener_complete` and
 * `phone_number_requested` are the exceptions, recording an action the
 * respondent took. The PostHog `precheck_*` stages in
 * components/interview/livekit/events.ts fire when a stage is *completed*, and
 * are emitted from different points in the same flow. The two systems therefore
 * do not produce matching numbers and their funnels will not line up — that is
 * known and intentional, not drift to be reconciled.
 */
export interface InterviewEvents {
  opened?: InterviewEventRecord;
  screener_start?: InterviewEventRecord;
  // `qualified` is the screener's evaluated outcome, recorded for both results
  // so a disqualification is distinguishable from an abandoned screener.
  screener_complete?: InterviewEventRecord & { qualified: boolean };
  pivot?: InterviewEventRecord;
  device_check?: InterviewEventRecord;
  // A consent surface was presented. Which surface differs by flow: the
  // dedicated ConsentStep on web, and PhoneInterview on phone — where, without
  // a screener, consent sits inline on the brief and so this lands at
  // effectively the same moment as `opened`.
  consent?: InterviewEventRecord;
  // Phone only: the respondent accepted consent and asked for a number. The
  // furthest the web session goes — the call is then dialed from their phone
  // and a conversation only exists once the agent answers it, so the gap
  // between this and started_at is people who got a number and never called.
  phone_number_requested?: InterviewEventRecord;
}

export type InterviewEventName = keyof InterviewEvents;

/**
 * The fields a given event carries beyond its timestamp — `{ qualified }` for
 * screener_complete, and empty for every other event. Object-literal checking
 * makes passing a payload to an event that takes none an error.
 */
export type InterviewEventData<E extends InterviewEventName> = Omit<
  NonNullable<InterviewEvents[E]>,
  'event_dt'
>;

// A single pronunciation override: whenever the TTS is about to speak `text`,
// it says `replacement` instead (e.g. "Corvus" → "Core-vus"). Alias-style
// replacement spellings only — the ElevenLabs models we run skip phoneme rules.
export interface PronunciationRule {
  text: string;
  replacement: string;
}

export interface VoiceConfig {
  voice_key?: string;
  voice_id_override?: string;
  // ElevenLabs playback speed; 1.0 is normal, supported range [0.8, 1.2].
  tts_speed?: number;
  // User-editable pronunciation overrides — the source of truth the
  // ElevenLabs dictionary below is derived from.
  pronunciations?: PronunciationRule[];
  // Locator of the ElevenLabs pronunciation dictionary created from
  // `pronunciations`. Server-derived on save (see
  // utils/elevenlabs/pronunciation.ts) — never edited directly.
  elevenlabs_pronunciation_dictionary?: {
    id: string;
    version_id: string;
  };
}

// ---------------------------------------------------------------------------
// Shared question primitive
//
// One question model used by two products: interview screeners and surveys.
// Screeners extend it with qualify/disqualify metadata (below); surveys use
// the base as-is. The choice fields keep the names the screener has always
// used (`selection`, `options`, `selected_option_*`) so the existing
// `interviews.screener_questions` / `conversation_metadata.screener_answers`
// JSON keeps validating with no data migration.
// ---------------------------------------------------------------------------

// 'single'/'multiple' are choice questions backed by `options`; 'text' is a
// free-response question with no options.
export type QuestionSelection = 'single' | 'multiple' | 'text';

export interface QuestionOption {
  id: string;
  label: string;
}

export interface Question {
  id: string;
  text: string;
  // Absent ⇒ 'single'.
  selection?: QuestionSelection;
  // Required for 'single'/'multiple'; omitted for 'text'.
  options?: QuestionOption[];
  // Whether the respondent must answer before continuing. Absent ⇒ optional.
  required?: boolean;
}

// One answered question. Choice questions populate the `selected_option_*`
// fields; text questions populate `response_text`.
export interface QuestionAnswer {
  question_id: string;
  question_text: string;
  selected_option_ids?: string[];
  selected_option_labels?: string[];
  response_text?: string;
}

// Screeners are choice-only — they never use the 'text' selection.
export type ScreenerSelection = 'single' | 'multiple';

// Per-option qualifying logic. Mirrors the campaign screener vocabulary
// (utils/agents/campaign-schemas.ts) so the two systems speak one language.
//   single-select   → 'Qualify' | 'Disqualify'
//   multiple-select → 'May Select' (neutral) | 'Must Select' | 'Disqualify'
// Absent ⇒ neutral: the option never disqualifies (capture-only screener).
export type ScreenerQualification =
  | 'Qualify'
  | 'Disqualify'
  | 'May Select'
  | 'Must Select';

export interface ScreenerOption extends QuestionOption {
  qualification?: ScreenerQualification;
}

export interface ScreenerQuestion
  extends Omit<Question, 'selection' | 'options'> {
  // Absent ⇒ 'single'. Single-select keeps tap-to-advance; multiple-select
  // lets the respondent pick several options and confirm with a button.
  selection?: ScreenerSelection;
  options: ScreenerOption[];
}

// Screener answers are the shared answer shape. (Previously had its own
// definition with required option fields; it's now the widened base, which is
// back-compatible for every existing reader.)
export type ScreenerAnswer = QuestionAnswer;

export interface ScreenerEvaluation {
  qualified: boolean;
  // The first question whose answer disqualified the respondent, when any.
  failedQuestionId?: string;
}

// ---------------------------------------------------------------------------
// Surveys — a first-class peer of interviews. A survey is a lean, form-based
// questionnaire with no conversational runtime. Its question flow is stored as
// this config object in `surveys.config` (jsonb).
// ---------------------------------------------------------------------------

export enum SurveyStatus {
  DRAFT = 10, // Editable, not collecting.
  LIVE = 20, // Actively collecting responses.
  COMPLETE = 21, // No longer collecting.
  ARCHIVED = 30 // Soft delete, not visible in portal.
}

export interface SurveyConfig {
  questions: Question[];
  // Optional global context/intro shown with the survey.
  context?: string;
}

export interface DossierAttachment {
  path: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

export enum ExtractionStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  UNSUPPORTED = 'unsupported'
}

export enum ClipType {
  QUESTION_ANSWER = 10,
  ANSWER_ONLY = 20,
  USER_GENERATED = 30
}

export enum DeviceType {
  DESKTOP = 10,
  MOBILE = 20
}

export enum ConversationRewardStatus {
  AWAITING_EMAIL = 10,
  AWAITING_APPROVAL = 20,
  SENT = 30,
  REJECTED = 40
}

/**
 * How a conversation list is narrowed by reward: to one reward status, or to
 * every conversation carrying a claim at all.
 */
export type RewardFilter = ConversationRewardStatus | 'any';

export type MediaArtifactType = 'conversation' | 'clip' | 'reel' | 'report';

/**
 * How a report's text reads. A style governs voice, length, and prose form
 * across the four fields a reader sees — the executive summary, and each
 * section's name, caption, and analysis. It reaches those fields as an insert
 * into the prompts that write them, and reaches nothing else: every style
 * produces the same sections, the same reels, the same signals, and the same
 * shape of report.
 *
 * Stored on the report rather than on the run that made it, so a section
 * rewritten weeks later is written in the voice the rest of the report already
 * speaks in.
 *
 * Numbered in tens like the other domain enums, so a style can be added
 * between two existing ones without renumbering what is already stored.
 */
export enum ReportStyle {
  /** Narrative prose, a paragraph or two per section. */
  STANDARD = 10,
  /**
   * Written to be skimmed by someone who will not read it all: the takeaway
   * first, bullets over paragraphs, and every section's caption sharpened from
   * a description of the section into the one thing to know about it.
   */
  EXECUTIVE = 20,
  /** Respondents as people, with scene and voice carried into the prose. */
  NARRATIVE = 30,
  /**
   * Every finding carried through to what the organization should do about it,
   * against the goals and problems named in the research brief. Leans harder on
   * that brief than any other style — where it is thin, this one has little to
   * reason from and says so rather than inventing an action.
   */
  ACTIONABLE = 40
}

/**
 * The style a report is written in when it does not name one, which is every
 * report generated before styles existed.
 */
export const DEFAULT_REPORT_STYLE = ReportStyle.STANDARD;

export interface SectionedReport {
  executive_summary: string;
  sections: SectionedReportSection[];
  /** Absent on reports written before styles existed — read it through
   *  `DEFAULT_REPORT_STYLE` rather than assuming it is set. */
  style?: ReportStyle;
}

export interface SectionedReportSection {
  slug: string;
  topic_guid?: string;
  name: string;
  caption: string;
  analysis: string;
  reel_id?: number | null;
  interview_codetype_ids: number[];
  metadata: Record<string, any>;
}

export enum SubscriptionManagedBy {
  NONE = 1,
  MANUAL = 2,
  STRIPE = 3
}
